import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cryptpe_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 402 = paywall. The backend returns:
//   { detail: { error: "tier_required", required_tier, current_tier, code, message } }
// Dispatches a global `cryptpe:paywall` event so a single in-app modal can
// surface a rich upgrade screen (richer than a transient toast). The toast
// stays as a fallback for non-React contexts (e.g. iframe widget) — but
// it dedupes per `code` so we never double-fire.
//
// 412 = email-verification required. The backend returns:
//   { detail: { error: "email_verification_required", action, email, message } }
// Dispatched as `cryptpe:verify-email` — the Dashboard listens and pops a
// "verify your email first" modal with a one-click "resend link" button.
let _lastPaywall = 0;
let _lastVerify = 0;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || {};

    // SEC-01 admin step-up: high-risk admin actions return 401
    // step_up_required / step_up_invalid. Prompt for the current TOTP code
    // and retry the original request once with the X-Admin-TOTP header.
    if (status === 401 && typeof detail === "string" && detail.startsWith("step_up")) {
      const { promptDialog } = await import("./confirm");
      const code = await promptDialog(
        detail === "step_up_invalid"
          ? "that code didn't verify — enter the current 6-digit code from your authenticator app"
          : "this action needs a second factor — enter the 6-digit code from your authenticator app",
        { confirmText: "verify", placeholder: "123456" },
      );
      if (code && /^\d{6}$/.test(code.trim())) {
        const cfg = { ...err.config, headers: { ...err.config.headers, "X-Admin-TOTP": code.trim() } };
        return api.request(cfg);
      }
      return Promise.reject(err);
    }

    // ─────────────────────────────────────────────────────────────────────
    // Cloudflare / gateway hiccup guard.
    //
    // When the origin is briefly unreachable (cold start, deploy, load
    // spike) Cloudflare returns its own HTML error page (520-524, 530)
    // with text like "The origin web server sent a response that
    // Cloudflare could not parse...". That HTML must NEVER end up
    // rendered verbatim in our UI. We replace the response body with a
    // structured detail so every downstream component (Login, Dashboard,
    // toasts) shows a friendly retry message instead.
    //
    // Also catches the case where ANY 5xx comes back as text/html
    // (proxies, load balancers, ingress timeouts) regardless of code.
    if (err && err.response) {
      const data = err.response.data;
      const isHtml = typeof data === "string" && /<\s*html/i.test(data);
      const isGatewayCode = [502, 503, 504, 520, 521, 522, 523, 524, 525, 530].includes(status);
      if (isGatewayCode || (status >= 500 && isHtml)) {
        err.response.data = {
          detail: "Server is temporarily unavailable. Please try again in a moment.",
        };
      }
    }

    if (status === 402) {
      if (typeof detail === "object" && detail.message) {
        const now = Date.now();
        if (now - _lastPaywall > 2500) {
          _lastPaywall = now;
          // Rich modal path — Dashboard listens and pops UpgradeModal
          try {
            window.dispatchEvent(new CustomEvent("cryptpe:paywall", {
              detail: {
                code: detail.code || "default",
                requiredTier: detail.required_tier || "pro",
                currentTier: detail.current_tier || "free",
                message: detail.message,
              },
            }));
          } catch (_) {}
          // Toast fallback — fires even when no listener is mounted
          // (e.g. before Dashboard hydrates). Suppressed when a listener
          // is known to be mounted via a window flag.
          if (!window.__cryptpe_paywall_listener__) {
            toast.error(detail.message, {
              action: {
                label: "Upgrade",
                onClick: () => { window.location.href = `/pricing?from=paywall-${detail.code || "default"}`; },
              },
            });
          }
        }
      }
    }

    if (status === 412 && typeof detail === "object"
        && detail.error === "email_verification_required") {
      const now = Date.now();
      if (now - _lastVerify > 2500) {
        _lastVerify = now;
        try {
          window.dispatchEvent(new CustomEvent("cryptpe:verify-email", {
            detail: {
              action: detail.action || "this action",
              email: detail.email || "",
              message: detail.message || "Please verify your email first.",
            },
          }));
        } catch (_) {}
        // Toast fallback when no in-app listener is mounted yet
        if (!window.__cryptpe_verify_listener__) {
          toast.error(detail.message || "Verify your email to continue.", {
            action: {
              label: "Resend link",
              onClick: async () => {
                try {
                  await api.post("/auth/send-verification");
                  toast.success("Verification email sent. Check your inbox.");
                } catch (_) {
                  toast.error("Could not send verification email. Try again later.");
                }
              },
            },
          });
        }
      }
    }

    return Promise.reject(err);
  },
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (detail === "totp_locked")
    return "Too many wrong 2FA codes — verification is locked for 15 minutes. Try again later.";
  if (detail === "too_many_failed_verifications")
    return "This order is locked after too many failed verification attempts. Contact support if you actually paid.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Structured error envelope. Components that did `formatApiError(detail) ||
  // e.message` used to fall through to String(detail) → "[object Object]"
  // for any backend response shaped like { error, message, ... }. Prefer
  // detail.message → detail.msg → detail.error before stringifying.
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim())
      return detail.message;
    if (typeof detail.msg === "string" && detail.msg.trim())
      return detail.msg;
    if (typeof detail.error === "string" && detail.error.trim())
      return detail.error;
    // Last resort — JSON-stringify rather than coerce to "[object Object]".
    try { return JSON.stringify(detail); } catch (_) {}
  }
  return String(detail);
}

/**
 * isHandledGlobally(err) — true when this axios error already triggered a
 * global UX surface (modal/toast) via the api.js interceptor. Components
 * should use this to suppress their own toast.error so the user doesn't see
 * a duplicate notification.
 *
 * Currently covers:
 *   - 402 paywall (UpgradeModal via cryptpe:paywall event)
 *   - 412 email_verification_required (VerifyEmailRequiredModal via
 *     cryptpe:verify-email event)
 */
export function isHandledGlobally(err) {
  const status = err?.response?.status;
  if (status === 402) return true;
  if (status === 412) {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object"
        && detail.error === "email_verification_required") {
      return true;
    }
  }
  return false;
}

export default api;
